// components/StaffDialog.tsx
import AsyncAutocompleteSingleSelectField from "@/components/form/components/AsyncAutocompleteSingleSelectField";
import AsyncSingleSelectField from "@/components/form/components/AsyncSingleSelectField";
import InputField from "@/components/form/components/InputField";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import {
  useCreateStaffMutation,
  useUpdateStaffMutation,
  useStaffByIdQuery,
} from "@/hooks/api/staff.queries";
import { departmentService } from "@/services/department.service";
import {
  fetchDesignationOptions,
  StaffCreateType,
  StaffParamsType,
} from "@/services/staff.service";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import * as z from "zod";
import { fetchRoleOptions } from "@/services/role.service";
import { rolesKey } from "@/hooks/api/role.queries";
import { departmentsKey } from "@/hooks/api/department.queries";
import { useAllDesignationsQuery } from "@/hooks/api/designation.queries";
import { Spinner } from "@/components";
import { isHodDesignation } from "@/utils/hod.utils";

type StaffDialogProps = {
  mode: "create" | "edit";
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: StaffParamsType;
  staffId?: string;
  onCancel: () => void;
  onSuccess?: (staff: { id: string; name: string }) => void;
  prefillDesignation?: { id: string; name: string };
};

const baseStaffSchema = z.object({
  name: z.string().min(1, "Full Name is required"),
  org_email: z
    .union([z.literal(""), z.email("Invalid organizational email address")])
    .optional(),
  personal_email: z.email("Invalid personal email address"),
  phone: z.string().min(1, "Phone Number is required"),
  department_id: z.string().optional(),
  designation_id: z.string().min(1, "Designation is required"),
  role_id: z.string().min(1, "Role is required"), // Added role_id
  employee_id: z.string().min(1, "Employee ID is required"),
  join_date: z.string().min(1, "Join Date is required"),
  qualification: z.string().min(1, "Qualification is required"),
  experience_years: z.coerce.number().min(0, "Experience must be 0 or more"),
});

type StaffFormType = z.infer<typeof baseStaffSchema>;

const getStaffSchema = (requiredDesignationIds: string[]) => {
  return baseStaffSchema.superRefine((data, ctx) => {
    if (requiredDesignationIds.includes(data.designation_id)) {
      if (!data.department_id || data.department_id.trim() === "") {
        ctx.addIssue({
          code: "custom",
          message: "Department is required for this designation",
          path: ["department_id"],
        });
      }
    }
  });
};

const StaffDialog = ({
  mode,
  isOpen,
  onOpenChange,
  filters,
  staffId,
  onCancel,
  onSuccess,
  prefillDesignation,
}: StaffDialogProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const createMutation = useCreateStaffMutation(filters);
  const updateMutation = useUpdateStaffMutation(filters);
  const isPending = createMutation.isPending || updateMutation.isPending;

  const { data: staffData, isLoading: isLoadingStaff } = useStaffByIdQuery(
    staffId || "",
    mode === "edit" && isOpen
  );

  const [injectedOption, setInjectedOption] = useState<{
    label: string;
    value: string;
    field: string;
  } | null>(null);

  const { data: designations = [] } = useAllDesignationsQuery();

  const requiredDesignationIds = useMemo(() => {
    return designations
      .filter((d) => d.name.toLowerCase() !== "admin")
      .map((d) => d.id);
  }, [designations]);

  const formSchema = useMemo(
    () => getStaffSchema(requiredDesignationIds),
    [requiredDesignationIds]
  );

  const form = useForm<StaffFormType>({
    resolver: zodResolver(formSchema) as any,
    defaultValues: {
      name: "",
      org_email: "",
      personal_email: "",
      phone: "",
      department_id: "",
      designation_id:
        mode === "create" && prefillDesignation ? prefillDesignation.id : "",
      role_id: "",
      employee_id: "",
      join_date: new Date().toISOString().split("T")[0],
      qualification: "",
      experience_years: 0,
    },
  });

  useEffect(() => {
    if (isOpen) {
      const INLINE_DRAFT_KEY = "inline_create_draft";
      const draftStr = sessionStorage.getItem(INLINE_DRAFT_KEY);
      let draftToRestore = null;
      let targetField = null;
      let newValue = null;

      let newLabel = null;

      if (draftStr) {
        try {
          const parsed = JSON.parse(draftStr);
          if (
            parsed.newValue &&
            parsed.sourcePath &&
            parsed.sourcePath.startsWith("/people/staff")
          ) {
            draftToRestore = parsed.draftData.values;
            targetField = parsed.targetField;
            newValue = parsed.newValue;
            newLabel = parsed.newLabel;
          }
        } catch (e) {
          console.error("Failed to parse inline draft on dialog open", e);
        }
      }

      if (draftToRestore && targetField && newValue) {
        if (newLabel) {
          setInjectedOption({
            label: newLabel,
            value: newValue,
            field: targetField,
          });
        }
        // Restore from returned draft and auto-fill the new entity ID
        setTimeout(() => {
          form.reset({
            ...draftToRestore,
            [targetField]: newValue,
          });
          sessionStorage.removeItem(INLINE_DRAFT_KEY);
        }, 50);
      } else if (mode === "edit" && staffData) {
        setTimeout(() => {
          form.reset({
            name: staffData.name,
            org_email: staffData.org_email,
            personal_email: staffData.personal_email,
            phone: staffData.phone,
            department_id: staffData.department_id ?? "",
            designation_id: staffData.designation_id,
            role_id: staffData.role_id ?? "",
            employee_id: staffData.employee_id ?? "",
            join_date: staffData.join_date ?? "",
            qualification: staffData.qualification ?? "",
            experience_years: staffData.experience_years || 0,
          });
        }, 0);
      } else {
        form.reset({
          name: "",
          org_email: "",
          personal_email: "",
          phone: "",
          department_id: "",
          designation_id:
            mode === "create" && prefillDesignation
              ? prefillDesignation.id
              : "",
          role_id: "",
          employee_id: "",
          join_date: new Date().toISOString().split("T")[0],
          qualification: "",
          experience_years: 0,
        });
      }
    }
  }, [mode, staffData, isOpen, form]);

  /**
   * After a staff member is saved, if they are an HOD and have a department,
   * we fetch the full department (to avoid overwriting its fields) and PUT back
   * with the correct hod_id set.
   */
  const syncHodWithDepartment = async (
    staffId: string,
    departmentId: string
  ) => {
    try {
      const dept = await departmentService.fetchDepartmentById(departmentId);
      if (!dept) return;
      await departmentService.updateDepartment(departmentId, {
        name: dept.name,
        code: dept.code,
        email: dept.email ?? "",
        phone: dept.phone ?? "",
        established_year: dept.established_year ?? 0,
        description: dept.description ?? "",
        status: dept.is_active ? "active" : "inactive",
        hod_id: staffId,
      });
      // Refresh department queries so HOD card updates
      queryClient.invalidateQueries({ queryKey: departmentsKey.all });
    } catch (err) {
      // Non-fatal: staff was saved correctly, HOD sync is best-effort
      console.error("HOD sync failed — department may need manual update", err);
    }
  };

  const handleSubmit = (values: StaffFormType) => {
    const submissionData: StaffCreateType = {
      ...values,
      org_email: values.org_email || undefined,
      department_id: values.department_id || undefined,
    };

    // Resolve the human-readable designation name so we can check for HOD
    const selectedDesignation = designations.find(
      (d) => d.id === values.designation_id
    );

    if (mode === "create") {
      createMutation.mutate(submissionData, {
        onSuccess: async (data: any) => {
          const createdStaff = data?.data || data;
          // If the new staff member is an HOD and has a department, sync it
          if (
            isHodDesignation(selectedDesignation?.name) &&
            values.department_id
          ) {
            await syncHodWithDepartment(createdStaff.id, values.department_id);
          }
          onSuccess?.({ id: createdStaff.id, name: createdStaff.name });
          onCancel();
        },
      });
    } else if (mode === "edit" && staffId) {
      updateMutation.mutate(
        { id: staffId, data: submissionData },
        {
          onSuccess: async () => {
            // If the edited staff member is an HOD and has a department, sync it
            if (
              isHodDesignation(selectedDesignation?.name) &&
              values.department_id
            ) {
              await syncHodWithDepartment(staffId, values.department_id);
            }
            onCancel();
          },
        }
      );
    }
  };

  const selectedDesignationId = form.watch("designation_id");
  const isDepartmentRequired = requiredDesignationIds.includes(
    selectedDesignationId
  );

  const defaultDepartmentOption = [
    ...(staffData?.department_id
      ? [
          {
            label: "Department",
            value: staffData.department_id,
          },
        ]
      : []),
    ...(injectedOption && injectedOption.field === "department_id"
      ? [injectedOption]
      : []),
  ];

  const defaultDesignationOption = staffData?.designation_id
    ? {
        label: staffData.designation_name || "Designation",
        value: staffData.designation_id,
      }
    : mode === "create" && prefillDesignation
      ? {
          label: prefillDesignation.name,
          value: prefillDesignation.id,
        }
      : undefined;

  const defaultStaffRoleOption = [
    ...(staffData?.role_id
      ? [
          {
            label: "Role",
            value: staffData.role_id,
          },
        ]
      : []),
    ...(injectedOption && injectedOption.field === "role_id"
      ? [injectedOption]
      : []),
  ];

  const INLINE_DRAFT_KEY = "inline_create_draft";

  const handleAddRole = () => {
    sessionStorage.setItem(
      INLINE_DRAFT_KEY,
      JSON.stringify({
        sourcePath: location.pathname + location.search,
        draftData: { values: form.getValues(), mode, staffId: staffId ?? "" },
        targetField: "role_id",
        isPendingReturn: true,
      })
    );
    navigate("/settings?tab=roles");
  };

  const handleAddDept = () => {
    sessionStorage.setItem(
      INLINE_DRAFT_KEY,
      JSON.stringify({
        sourcePath: location.pathname + location.search,
        draftData: { values: form.getValues(), mode, staffId: staffId ?? "" },
        targetField: "department_id",
        isPendingReturn: true,
      })
    );
    navigate("/department");
  };

  const handleCancel = () => {
    sessionStorage.removeItem(INLINE_DRAFT_KEY);
    onCancel();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) sessionStorage.removeItem(INLINE_DRAFT_KEY);
    onOpenChange(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        onInteractOutside={(e) => {
          // Prevent closing when clicking outside
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Add New Staff" : "Edit Staff Member"}
          </DialogTitle>
        </DialogHeader>

        {mode === "edit" && isLoadingStaff ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="grid grid-cols-1 gap-4 py-2 md:grid-cols-2"
            >
              {/* Full Name */}
              <InputField<StaffFormType>
                name="name"
                label="Full Name"
                placeholder="e.g. Prof. Michael Brown"
                type="text"
                disabled={isPending}
                required
              />

              {/* Employee ID */}
              <InputField<StaffFormType>
                name="employee_id"
                label="Employee ID"
                placeholder="e.g. STF001"
                type="text"
                disabled={isPending}
                required
              />

              {/* Org Email */}
              <InputField<StaffFormType>
                name="org_email"
                label="Organizational Email"
                placeholder="e.g. michael.brown@college.edu"
                type="email"
                disabled={isPending}
              />

              {/* Personal Email */}
              <InputField<StaffFormType>
                name="personal_email"
                label="Personal Email"
                placeholder="e.g. michael.b@gmail.com"
                type="email"
                disabled={isPending}
                required
              />

              {/* Phone */}
              <InputField<StaffFormType>
                name="phone"
                label="Phone Number"
                placeholder="e.g. 9876543210"
                type="tel"
                inputMode="numeric"
                maxLength={10}
                disabled={isPending}
                required
              />

              {/* Department - Async Select */}
              <AsyncSingleSelectField<StaffFormType>
                name="department_id"
                label="Department"
                placeholder="Select department"
                fetchOptions={departmentService.fetchDepartmentOptions}
                queryKey={[...departmentsKey.all]}
                defaultOptions={defaultDepartmentOption}
                disabled={isPending}
                required={isDepartmentRequired}
                onCreateClick={handleAddDept}
                createLabel="Add Department"
              />

              {/* Designation - Async Autocomplete */}
              <AsyncAutocompleteSingleSelectField<StaffFormType>
                name="designation_id"
                label="Designation"
                placeholder="Select designation"
                fetchOptions={async (params) => {
                  const res = await fetchDesignationOptions({
                    search: params.search,
                    page: 1,
                    pageSize: params.limit,
                  } as any);
                  return res.items;
                }}
                queryKey={["designations"]}
                defaultOption={defaultDesignationOption}
                disabled={true}
                required
              />

              {/* Role - Async Select */}
              <AsyncSingleSelectField<StaffFormType>
                name="role_id"
                label="Role"
                placeholder="Select role"
                fetchOptions={fetchRoleOptions}
                queryKey={[...rolesKey.all]}
                defaultOptions={defaultStaffRoleOption}
                disabled={isPending}
                onCreateClick={handleAddRole}
                createLabel="Add Role"
                required
              />

              {/* Join Date */}
              <InputField<StaffFormType>
                name="join_date"
                label="Join Date"
                type="date"
                disabled={isPending}
                required
              />

              {/* Qualification */}
              <InputField<StaffFormType>
                name="qualification"
                label="Qualification"
                placeholder="e.g. M.Tech, Ph.D."
                type="text"
                disabled={isPending}
                required
              />

              {/* Experience */}
              <InputField<StaffFormType>
                name="experience_years"
                label="Experience (years)"
                placeholder="e.g. 10"
                type="number"
                disabled={isPending}
                required
              />

              <DialogFooter className="md:col-span-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending
                    ? mode === "create"
                      ? "Adding..."
                      : "Saving..."
                    : mode === "create"
                      ? "Create Staff"
                      : "Save Changes"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StaffDialog;
